import type {
  ChallengeBand,
  ChallengeCandidatesResponse,
  ChallengeListResponse,
  ChallengeSummary,
  CreateChallengeInput,
  Format,
} from '@foosball/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/client';

export function useChallenges() {
  return useQuery({
    queryKey: ['challenges', 'list'],
    queryFn: () => api.get<ChallengeListResponse>('/api/challenges'),
  });
}

/** Drives the nav badge, on the same 60s cadence as the confirmations one. */
export function useChallengesAwaitingMe() {
  return useQuery({
    queryKey: ['challenges', 'awaiting-me'],
    queryFn: () => api.get<{ count: number }>('/api/challenges/awaiting-me'),
    refetchInterval: 60_000,
  });
}

export function useChallengeCandidates(format: Format, band: ChallengeBand | null) {
  const search = new URLSearchParams({ format });
  if (band) search.set('band', band);

  return useQuery({
    queryKey: ['challenges', 'candidates', format, band],
    queryFn: () => api.get<ChallengeCandidatesResponse>(`/api/challenges/candidates?${search}`),
    // Ratings shift as results land, and the bands with them.
    staleTime: 30_000,
  });
}

/**
 * Every challenge mutation moves a row between the incoming/outgoing/scheduled
 * lists and changes the badge, so both keys go at once.
 */
function useChallengeInvalidation() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['challenges'] });
  };
}

export function useCreateChallenge() {
  const invalidate = useChallengeInvalidation();

  return useMutation({
    mutationFn: (input: CreateChallengeInput) =>
      api.post<{ challenge: ChallengeSummary }>('/api/challenges', input),
    onSuccess: invalidate,
  });
}

export function useAnswerChallenge() {
  const invalidate = useChallengeInvalidation();

  return useMutation({
    mutationFn: ({ id, answer }: { id: number; answer: 'accept' | 'decline' }) =>
      api.post<{ challenge: ChallengeSummary }>(`/api/challenges/${id}/${answer}`),
    onSuccess: invalidate,
  });
}

export function useCancelChallenge() {
  const invalidate = useChallengeInvalidation();

  return useMutation({
    mutationFn: (id: number) => api.delete<{ challenge: ChallengeSummary }>(`/api/challenges/${id}`),
    onSuccess: invalidate,
  });
}
